// === SAA:UI:GLASS-PREMIUM ===
// Glass (GP) is rated by the Midglas calculation engine, not by this app. Both Woonhuis and
// Inboedel send the GP block with GP_MYAAND = 'M027' (POR maatschappij-aanduiding, codelist
// PORMAA) and GP_CODE = '5021', plus BA_HERBWRD as the rebuild value the tariff runs over.
// The engine returns the GP premium fields; nothing here computes them.
//
// This module holds the wire constants and the product-selection gate.

export const GLASS_COVERAGE_CODE = '5021';

/** POR maatschappij-aanduiding routing GP to the Midglas tariff (codelist 43E/PORMAA). */
export const GLASS_MYAAND = 'M027';

export interface GlassPremiumConfig {
  coveredByProducts?: string[];
  disabledForProducts?: string[];
}

export interface GlassRequestBlock {
  GP_CODE: string;
  GP_MYAAND: string;
}

/**
 * GP section for a calculation request. Carries only the fields the engine needs to route to
 * the Midglas tariff; the premium fields come back on the response.
 */
export const buildGlassRequestBlock = (): GlassRequestBlock => ({
  GP_CODE: GLASS_COVERAGE_CODE,
  GP_MYAAND: GLASS_MYAAND,
});

const DEFAULT_COVERED_BY_PRODUCTS = ['woonhuis'];

/**
 * Product ids whose selection already includes glass (glass rides on the building product).
 * When any of them is selected, the Inboedel Glas toggle is hidden and forced off so the
 * customer can't buy the same cover twice. The list lives in
 * `config.glassPremium.coveredByProducts` so a renamed or added product on the selection page
 * is a config edit, not a code change.
 */
export const glassCoveredByOtherProduct = (
  selectedProducts: Record<string, boolean | undefined> | null | undefined,
  config: unknown,
): boolean => {
  const cfg = config as { glassPremium?: GlassPremiumConfig } | null | undefined;
  const list = cfg?.glassPremium?.coveredByProducts ?? DEFAULT_COVERED_BY_PRODUCTS;
  return list.some((productId) => Boolean(selectedProducts?.[productId]));
};

/**
 * Products where the glass cover is switched off entirely
 * (`config.glassPremium.disabledForProducts`). Emergency brake only: the per-address
 * rule that keeps an unratable GP off the wire is `inboedelGlassAllowed` below.
 */
export const glassDisabledForProduct = (
  productId: string,
  config: unknown,
): boolean => {
  const cfg = config as { glassPremium?: GlassPremiumConfig } | null | undefined;
  return (cfg?.glassPremium?.disabledForProducts ?? []).includes(productId);
};

/** sessionStorage key the Inboedel screen persists its Infofolio-filled field names under. */
export const INBOEDEL_INFOFOLIO_FIELDS_KEY = 'inboedel_infofolio_fields';
/** sessionStorage key: address key of the last Inboedel Infofolio lookup that finished (data, none or error). */
export const INBOEDEL_INFOFOLIO_LOOKUP_DONE_KEY = 'inboedel_infofolio_lookup_done';

/**
 * Infofolio-filled field names of the Inboedel screen, as persisted by its useInfofolio hook.
 * Empty when nothing was filled or no storage is available (node tests).
 */
export const readInboedelInfofolioFields = (): ReadonlySet<string> => {
  try {
    const saved = globalThis.sessionStorage?.getItem(INBOEDEL_INFOFOLIO_FIELDS_KEY);
    const parsed = saved ? JSON.parse(saved) : null;
    return new Set(Array.isArray(parsed) ? parsed.filter((f): f is string => typeof f === 'string') : []);
  } catch {
    return new Set();
  }
};

export const infofolioAddressKey = (
  ba: { BA_PCODE?: unknown; BA_HUISNR?: unknown; BA_TOEVOEG?: unknown } | null | undefined,
): string => (ba?.BA_PCODE && ba?.BA_HUISNR ? `${ba.BA_PCODE}|${ba.BA_HUISNR}|${ba.BA_TOEVOEG || ''}` : '');

/** Infofolio field names that together mean the glass carrier knows the address. */
export const GLASS_LOOKUP_FIELDS = ['herbwrd', 'inboedelwaarde'] as const;

/**
 * Whether the Infofolio lookup supplied what the Midglas engine needs. Midglas rates Inboedel
 * glass over its OWN herbouwwaarde source and declines any address that source does not know,
 * taking the WHOLE calculation down with it (XG 11), so the customer loses every price, not
 * just glass. An address for which Infofolio returned both the herbouwwaarde and the
 * inboedelwaarde is the proxy for "known"; a value the customer typed is not.
 */
export const infofolioProvidesGlassRebuildValue = (fields: ReadonlySet<string>): boolean =>
  GLASS_LOOKUP_FIELDS.every((field) => fields.has(field));

/**
 * The ONE answer to "may Inboedel sell glass right now": the config switch is off, no selected
 * product already carries glass (Woonhuis rates its own glass over OP_VERZSOM, which needs no
 * lookup), and Infofolio supplied the rebuild and contents values for this address. Every wire
 * boundary that can put PP.GP on an Inboedel request asks this, so a GP the screen withheld can
 * never be rebuilt downstream.
 */
export const inboedelGlassAllowed = (
  config: unknown,
  selectedProducts: Record<string, boolean | undefined> | null | undefined,
  infofolioFields: ReadonlySet<string> = readInboedelInfofolioFields(),
): boolean =>
  !glassDisabledForProduct('inboedel', config)
  && !glassCoveredByOtherProduct(selectedProducts, config)
  && infofolioProvidesGlassRebuildValue(infofolioFields);

/**
 * Glass is unavailable ONLY because Infofolio does not know this address (switch on, no other
 * selected product carrying glass). The frontend never fills the herbouwwaarde itself, so the
 * customer is pointed to the tenant instead. False for every other reason the tile is hidden,
 * so the note never sits next to a Woonhuis that already includes glass.
 */
export const inboedelGlassNeedsContact = (
  config: unknown,
  selectedProducts: Record<string, boolean | undefined> | null | undefined,
  infofolioFields: ReadonlySet<string> = readInboedelInfofolioFields(),
): boolean =>
  !glassDisabledForProduct('inboedel', config)
  && !glassCoveredByOtherProduct(selectedProducts, config)
  && !infofolioProvidesGlassRebuildValue(infofolioFields);

/**
 * Herbouwwaarde (rebuild value) the Midglas engine rates Inboedel glass over. Lives at
 * `Contractdocument.PP.BA.BA_HERBWRD`; `_uiState` is the fallback for step data saved before
 * BA_HERBWRD was accepted on the Inboedel wire. Returns 0 when no usable value is present.
 */
export const readInboedelGlassAmount = (data: any): number => {
  const amount = Number(
    data?.Contractdocument?.PP?.BA?.BA_HERBWRD ?? data?._uiState?.BA_HERBWRD,
  );
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

/**
 * Whether a herbouwwaarde is usable as the Midglas rating basis.
 *
 * BA_HERBWRD is MANDATORY once PP.GP is on the request: without it the engine
 * declines the WHOLE calculation (XG 11, "BA_HERBWRD, Herbouwwaarde is een
 * verplicht veld voor Midglas Glas dekking"), so the product loses every
 * price, not just glass. Callers gate the GP section on this so an unknown
 * rebuild value costs glass its premium and nothing else. 0 and absent read
 * the same way here because the engine rejects both.
 */
export const hasGlassRebuildValue = (amount: unknown): boolean => {
  const value = Number(amount);
  return Number.isFinite(value) && value > 0;
};
